<template>
  <div v-if="user" class="user-button">
    <v-menu v-model="showMenu" offset-y nudge-bottom="10"
    content-class="user-menu-content"
    :close-on-content-click="false" min-width="268" left>
      <template v-slot:activator="{ on }">
        <button v-on="on" class="user-avatar" :class="{ 'is-open': showMenu }" :aria-label="displayName">
          <img v-if="photo" :src="photo" alt="" class="user-avatar-img" @error="photoFailed = true" />
          <template v-else>{{ initial }}</template>
        </button>
      </template>

      <div class="user-menu lp-card">
        <div class="user-menu-head">
          <span class="user-avatar user-avatar--sm">
            <img v-if="photo" :src="photo" alt="" class="user-avatar-img" />
            <template v-else>{{ initial }}</template>
          </span>
          <div class="user-menu-id">
            <span class="user-menu-name">{{ displayName }}</span>
            <span class="user-menu-email">{{ user.email }}</span>
          </div>
        </div>

        <div class="user-menu-sep"></div>

        <nav class="user-menu-list">
          <button class="user-menu-item" @click="goAccount">
            <l-icon size="15">gauge</l-icon>
            <span>{{ $t('dashboard.title') }}</span>
          </button>
          <button class="user-menu-item" @click="openPricing">
            <l-icon size="15">mdi-credit-card</l-icon>
            <span>{{ $t('nav.pricing') }}</span>
          </button>
          <button class="user-menu-item user-menu-item--muted" @click="signOut">
            <l-icon size="15">mdi-logout</l-icon>
            <span>{{ $t('dashboard.account.signOut') }}</span>
          </button>
        </nav>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { resolveSessionUser, logout, clearSessionCookies } from '@/services/nukliApi';

export default {
  data: () => ({ showMenu: false, photoFailed: false }),
  computed: {
    user() {
      return resolveSessionUser(this.$root);
    },
    displayName() {
      return this.user?.displayName || this.user?.email?.split('@')[0] || '';
    },
    initial() {
      return (this.displayName || '?').substring(0, 1);
    },
    // Foto del proveedor si el login social la devuelve (el nombre del campo
    // depende del backend, de ahí los tres). Si no llega —o el enlace caduca,
    // que a Google le pasa— se cae a la inicial sin romper nada.
    photo() {
      if (this.photoFailed || !this.user) return null;
      return this.user.picture || this.user.avatarUrl || this.user.photoUrl || null;
    },
  },
  methods: {
    goAccount() {
      this.showMenu = false;
      if (this.$route.name !== 'Account') this.$router.push('/account').catch(() => {});
    },
    openPricing() {
      this.showMenu = false;
      this.$root.$emit('open-pricing');
    },
    async signOut() {
      await logout();
      clearSessionCookies(this.$cookies);
      this.$root.nukliUser = null;
      this.showMenu = false;
      if (this.$route.path !== '/') await this.$router.push('/').catch(() => {});
    },
  },
};
</script>

<style scoped>
.user-button { display: inline-flex; align-items: center; }

/* Avatar neutro, mismo lenguaje que .icon-btn del AppBar (sin gradientes;
   el azul queda reservado para el CTA). */
.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid var(--lp-line-strong);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  color: var(--lp-ink-2);
  background: var(--lp-bg-subtle);
  transition: border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
  padding: 0;
}
.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.user-avatar:hover { border-color: var(--lp-ink); color: var(--lp-ink); }
.user-avatar.is-open {
  border-color: var(--lp-accent);
  color: var(--lp-ink);
  box-shadow: 0 0 0 3px var(--lp-accent-weak);
}

/* Dropdown */
.user-menu {
  padding: 6px;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  box-shadow: var(--lp-shadow-lg);
  border-radius: 14px;
  overflow: hidden;
}
.user-menu-head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 10px 12px;
}
.user-avatar--sm {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  font-size: 0.8rem;
  cursor: default;
}
.user-menu-id { display: flex; flex-direction: column; min-width: 0; }
.user-menu-name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--lp-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-menu-email {
  font-size: 0.8rem;
  color: var(--lp-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-menu-sep { height: 1px; background: var(--lp-line); margin: 0 4px 6px; }

.user-menu-list { display: flex; flex-direction: column; gap: 2px; }
.user-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 9px 10px;
  border-radius: 9px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--lp-ink-2);
  text-align: left;
  transition: background 0.12s ease, color 0.12s ease;
}
.user-menu-item .l-icon { color: var(--lp-muted); }
.user-menu-item:hover { background: var(--lp-bg-subtle); color: var(--lp-ink); }
.user-menu-item:hover .l-icon { color: var(--lp-ink); }
.user-menu-item--muted { color: var(--lp-muted); }

.user-menu-content {
  border-radius: 14px !important;
}
</style>
